import { EntityCommonInfoModel, FilteredPagedQueryResult, FilteredPagedModel, CommonMultiDeleteModel } from "../../entities/common";
import { apiSlice, TagTypes } from "./apiSlice";
import { getQuerySearchParams } from "../../utils";
import { InventoryTurnoverModel } from "../../entities/inventoryTurnover";

export const inventoryTurnoverApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getInventoryTurnoverCommonInfo: builder.query<EntityCommonInfoModel, boolean>({
      query: (isEstimated) => `/inventoryTurnover/${isEstimated}/commonInfo`,
      providesTags: [{ type: TagTypes.InventoryTurnover, id: "LIST" }],
    }),
    getInventoryTurnoverFilteredPaged: builder.query<FilteredPagedQueryResult<InventoryTurnoverModel>, FilteredPagedModel>({
      query: ({ isEstimated, page = 1, pageSize = 10, searchTerm = "", sortFieldName = "", descending = false, ...rest }: FilteredPagedModel) => {
        let url = `/inventoryTurnover/${isEstimated}/pageable?page=${page}&pageSize=${pageSize}&searchTerm=${searchTerm}&sortFieldName=${sortFieldName}&descending=${descending}`;
        const restParams = getQuerySearchParams(rest);
        if (restParams) {
          url += `&${restParams}`;
        }

        return url;
      },
      providesTags: (result, error, arg) =>
        result
          ? [
              { type: TagTypes.InventoryTurnover, id: "PARTIAL-LIST" },
              ...result.data.map(({ id }: { id: number | string }) => ({
                type: TagTypes.InventoryTurnover,
                id,
              })),
            ]
          : [{ type: TagTypes.InventoryTurnover, id: "PARTIAL-LIST" }],
    }),
    getInventoryTurnover: builder.query<InventoryTurnoverModel, number>({
      query: (inventoryTurnoverId) => `/inventoryTurnover/${inventoryTurnoverId}`,
      providesTags: (result, error, arg) => [{ type: TagTypes.InventoryTurnover, id: arg }],
    }),
    scrapInventoryTurnover: builder.query<InventoryTurnoverModel, { isEstimated: boolean; tracingNumber: string }>({
      query: ({ isEstimated, tracingNumber }) => ({
        url: `/inventoryTurnover/scrap/${isEstimated}`,
        method: "POST",
        body: { tracingNumber },
      }),
      providesTags: [{ type: TagTypes.InventoryTurnover, id: "Scrap" }],
    }),
    addInventoryTurnover: builder.mutation<InventoryTurnoverModel, InventoryTurnoverModel>({
      query: (initialInventoryTurnover) => ({
        url: "/inventoryTurnover",
        method: "POST",
        body: initialInventoryTurnover,
      }),
      invalidatesTags: [
        { type: TagTypes.InventoryTurnover, id: "LIST" },
        { type: TagTypes.InventoryTurnover, id: "PARTIAL-LIST" },
      ],
    }),
    editInventoryTurnover: builder.mutation<InventoryTurnoverModel, InventoryTurnoverModel>({
      query: (inventoryTurnover) => ({
        url: `/inventoryTurnover/${inventoryTurnover.id}`,
        method: "PUT",
        body: inventoryTurnover,
      }),
      invalidatesTags: (result, error, arg) => [{ type: TagTypes.InventoryTurnover, id: arg.id }],
    }),
    deleteInventoryTurnover: builder.mutation({
      query: (inventoryTurnoverId: number) => ({
        url: `/inventoryTurnover/${inventoryTurnoverId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              { type: TagTypes.InventoryTurnover, id: arg },
              { type: TagTypes.InventoryTurnover, id: "LIST" },
            ],
    }),
    deleteMultiInventoryTurnovers: builder.mutation({
      query: (multiDeleteModel: CommonMultiDeleteModel) => ({
        url: "/inventoryTurnover/multiDelete",
        method: "DELETE",
        body: multiDeleteModel,
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              ...arg.ids.map((id: number) => ({
                type: TagTypes.InventoryTurnover,
                id,
              })),
              { type: TagTypes.InventoryTurnover, id: "LIST" },
            ],
    }),
  }),
});

export const {
  useGetInventoryTurnoverCommonInfoQuery,
  useGetInventoryTurnoverFilteredPagedQuery,
  useGetInventoryTurnoverQuery,
  useAddInventoryTurnoverMutation,
  useEditInventoryTurnoverMutation,
  useScrapInventoryTurnoverQuery,
  useLazyScrapInventoryTurnoverQuery,
  useDeleteInventoryTurnoverMutation,
  useDeleteMultiInventoryTurnoversMutation,
} = inventoryTurnoverApi;
